import { useCallback, useEffect, useRef, useState } from 'react';
/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx } from '@emotion/core';
import * as Dialog from '@radix-ui/react-dialog';
import isFunction from 'lodash/isFunction';

import { useHideModal } from 'src/utils/modal/useModal';

import { style } from './Modal.style';

export interface Props {
  children: React.ReactNode | ((data: { hide: () => void }) => React.ReactNode);
  onCancel?: () => void;
  disableClickAway?: boolean;
  alpha?: number;
  width?: number;
}

// iOS Safari resolves fixed-position percentage placement (top: 50%) and
// vh/vw units against the large layout viewport, which does not shrink for
// the bottom toolbar: tall modals grow behind the toolbar and their footer
// becomes unreachable (the overlay cannot scroll fixed-position content).
// visualViewport reports the actual visible height, so the dialog can be
// centred and clamped fully above the toolbar.
//
// The on-screen keyboard, however, also shrinks the visual viewport, and
// re-centring on that reduced height throws the modal halfway up the page.
// Ignore keyboard-sized drops in height while the width is unchanged: the
// bottom toolbar only ever trims a small fixed strip, so it still passes
// through, and when the keyboard hides a resize fires again with the
// original height and the measurement is restored.
const KEYBOARD_MIN_HEIGHT = 150;

interface StableViewport {
  width: number;
  height?: number;
}

function resolveStableViewport(current: StableViewport): StableViewport {
  const width = window.innerWidth;
  const height = window.visualViewport?.height || window.innerHeight;

  if (height <= 0) {
    return current;
  }

  const keyboardSizedDrop = current.height !== undefined && current.height - height > KEYBOARD_MIN_HEIGHT;

  if ((keyboardSizedDrop && width === current.width) || (width === current.width && current.height === height)) {
    return current;
  }

  return { width, height };
}

export const Modal = ({ children, onCancel, disableClickAway, alpha = 0.65, width }: Props) => {
  const previousElement = useRef<any>(null);
  const hide = useHideModal();
  const cancel = useCallback(() => {
    hide();
    if (onCancel) {
      onCancel();
    }
  }, [hide, onCancel]);

  useEffect(() => {
    // keeps track of the previously focused element
    previousElement.current = document.activeElement;
  }, []);

  // See resolveStableViewport: keyboard-aware measured height.
  const [stableViewport, setStableViewport] = useState(() => resolveStableViewport({ width: -1 }));

  useEffect(() => {
    const viewport = window.visualViewport;
    const update = () => setStableViewport((current) => resolveStableViewport(current));

    update();

    viewport?.addEventListener('resize', update);
    viewport?.addEventListener('scroll', update);
    window.addEventListener('resize', update);

    return () => {
      viewport?.removeEventListener('resize', update);
      viewport?.removeEventListener('scroll', update);
      window.removeEventListener('resize', update);
    };
  }, []);

  return (
    <Dialog.Root open>
      <Dialog.Portal>
        <Dialog.Overlay css={style.overlay} onClick={cancel} />
        <Dialog.Content
          // required due to incompatibility with react-select focus logic
          tabIndex={undefined}
          onCloseAutoFocus={() => {
            previousElement.current?.focus();
          }}
          css={style.content({ width, viewportHeight: stableViewport.height })}
          onEscapeKeyDown={cancel}
        >
          {isFunction(children) ? children({ hide }) : children}

          <div id="modal-radix-root" />
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};
